import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  completeSession,
  openSession,
  recordEvent,
  rememberToken,
  saveAnswer,
  saveContact,
  saveProgress,
  submitGiftClaim,
} from "./api";
import { AdminDashboard } from "./components/AdminDashboard";
import { ConceptDemo } from "./components/ConceptDemo";
import {
  GIFT_AMOUNT,
  otherTextQuestionId,
  optionsForStep,
  visibleSurveySteps,
  warrantySurvey,
} from "./survey";
import type {
  AnswerValue,
  Answers,
  ContactPayload,
  GiftPayload,
  JourneyEventName,
  SafeEventMetadata,
  SessionState,
  SurveyStep,
  Variant,
} from "./types";

const emptyContact: ContactPayload = {
  name: "",
  company: "",
  followUpEmail: "",
  phone: "",
  preferredMethod: "",
  researchConsent: false,
  marketingConsent: false,
};

const emptyGift: GiftPayload = { name: "", company: "", giftEmail: "" };

function readVariant(): Variant {
  const value = new URLSearchParams(window.location.search).get("variant")?.toUpperCase();
  return value === "B" ? "B" : "A";
}

function isAnswered(value: AnswerValue | undefined): boolean {
  return value !== undefined && value !== "" && (!Array.isArray(value) || value.length > 0);
}

function OEMReadyLogo({ compact = false }: { compact?: boolean }) {
  return <span className={`brand-logo ${compact ? "compact" : ""}`} role="img" aria-label="OEMReady by SWMB Systems" />;
}

function Header({ step, index, total, onBack, canGoBack }: { step: SurveyStep; index: number; total: number; onBack: () => void; canGoBack: boolean }) {
  const progress = total > 1 ? Math.round((index / (total - 1)) * 100) : 100;
  return (
    <header className="survey-header">
      <div className="header-row">
        {canGoBack ? <button className="icon-button" onClick={onBack} aria-label="Go back">‹</button> : <span />}
        <OEMReadyLogo compact />
        <span />
      </div>
      <div className="progress-label"><span>{step.section}</span><span>{progress}% complete</span></div>
      <div className="progress-track" aria-label={`${progress}% complete`}><i style={{ width: `${progress}%` }} /></div>
    </header>
  );
}

function OptionButton({ label, selected, multi, onClick }: { label: string; selected: boolean; multi?: boolean; onClick: () => void }) {
  return (
    <button className={`option-button ${selected ? "selected" : ""}`} type="button" onClick={onClick} aria-pressed={selected}>
      {multi && <span className="check-box" aria-hidden="true">{selected ? "✓" : ""}</span>}
      <span>{label}</span>
      {!multi && <span className="option-arrow" aria-hidden="true">›</span>}
    </button>
  );
}

export default function App() {
  if (window.location.pathname.startsWith("/admin")) return <AdminDashboard />;
  return <SurveyApp />;
}

function SurveyApp() {
  const variant = useMemo(readVariant, []);
  const [session, setSession] = useState<SessionState | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [pendingSaves, setPendingSaves] = useState(0);
  const [saveError, setSaveError] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [demoReady, setDemoReady] = useState(false);
  const [contact, setContact] = useState<ContactPayload>(emptyContact);
  const [gift, setGift] = useState<GiftPayload>(emptyGift);
  const [giftStatus, setGiftStatus] = useState("");
  const viewedKey = useRef("");
  const completedRef = useRef(false);
  const textTimer = useRef<number | undefined>(undefined);

  const steps = useMemo(() => visibleSurveySteps(answers), [answers]);
  const safeIndex = Math.min(index, steps.length - 1);
  const step = steps[safeIndex];

  useEffect(() => {
    let cancelled = false;
    openSession(variant).then((opened) => {
      if (cancelled) return;
      rememberToken(variant, opened);
      setSession(opened);
      setAnswers(opened.answers ?? {});
      setSkipped(new Set(opened.skipped ?? []));
      setIndex(Math.min(opened.lastStep ?? 0, warrantySurvey.length - 1));
      setLoading(false);
    }).catch((error: Error) => {
      if (cancelled) return;
      setFatalError(`We couldn’t start a secure session. ${error.message}`);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [variant]);

  const sendEvent = useCallback(async (eventName: JourneyEventName, metadata?: SafeEventMetadata, override?: { questionId?: string; stepIndex?: number }) => {
    if (!session) return;
    try {
      await recordEvent(session, eventName, {
        stepIndex: override?.stepIndex ?? safeIndex,
        questionId: override?.questionId ?? step?.id,
        metadata,
      });
    } catch {
      // Journey events are retriable telemetry. Answer persistence reports errors visibly.
    }
  }, [safeIndex, session, step?.id]);

  useEffect(() => {
    if (!session || !step) return;
    const key = `${session.sessionId}:${step.id}:${safeIndex}`;
    if (viewedKey.current === key) return;
    viewedKey.current = key;
    void saveProgress(session, safeIndex).catch(() => undefined);
    if (["single", "multi", "ranked", "short", "nextstep"].includes(step.type)) void sendEvent("question_viewed");
    else if (step.type === "contact") void sendEvent("contact_viewed");
    else if (step.type === "giftcard") void sendEvent("gift_claim_viewed");
  }, [safeIndex, sendEvent, session, step, variant]);

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void sendEvent("page_hidden", { last_step: safeIndex });
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [safeIndex, sendEvent]);

  useEffect(() => {
    if (!session || step?.type !== "completion" || completedRef.current) return;
    completedRef.current = true;
    const screenedOut = step.id === "screened_out";
    void completeSession(session, screenedOut);
  }, [session, step]);

  const persistAnswer = useCallback(async (questionId: string, answer: AnswerValue | null, isSkipped: boolean, stepIndex = safeIndex) => {
    if (!session) return;
    setPendingSaves((count) => count + 1);
    setSaveError("");
    try {
      await saveAnswer(session, questionId, answer, isSkipped, stepIndex);
      setLastSavedAt(new Date());
    } catch {
      setSaveError("Not saved yet—check your connection and try again.");
      throw new Error("Save failed");
    } finally {
      setPendingSaves((count) => Math.max(0, count - 1));
    }
  }, [safeIndex, session]);

  const next = useCallback(() => {
    setValidationError("");
    setIndex((current) => Math.min(current + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [steps.length]);

  const back = useCallback(() => {
    void sendEvent("step_back", { from_step: safeIndex });
    setValidationError("");
    setIndex((current) => Math.max(0, current - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [safeIndex, sendEvent]);

  const clearOtherText = (questionId: string) => {
    const textId = otherTextQuestionId(questionId);
    setAnswers((state) => { const copy = { ...state }; delete copy[textId]; return copy; });
    void persistAnswer(textId, null, true).catch(() => undefined);
  };

  const chooseSingle = async (question: SurveyStep, optionId: string) => {
    const questionId = question.id;
    const otherOption = question.options?.find((option) => option.label.toLowerCase() === "other");
    const nextAnswers = { ...answers, [questionId]: optionId };
    setAnswers(nextAnswers);
    setSkipped((current) => { const copy = new Set(current); copy.delete(questionId); return copy; });
    setValidationError("");
    try {
      await persistAnswer(questionId, optionId, false);
      if (optionId === otherOption?.id) return;
      if (answers[questionId] === otherOption?.id) clearOtherText(questionId);
      window.setTimeout(next, 120);
    } catch {
      // Remain on the question until the answer is safely stored.
    }
  };

  const toggleMulti = (question: SurveyStep, optionId: string) => {
    const current = Array.isArray(answers[question.id]) ? answers[question.id] as string[] : [];
    const exclusiveIds = new Set(
      (question.options ?? [])
        .filter((option) => ["none of these", "no real concerns", "no follow-up"].includes(option.label.toLowerCase()))
        .map((option) => option.id),
    );
    const otherOption = question.options?.find((option) => option.label.toLowerCase() === "other");
    let updated: string[];
    if (exclusiveIds.has(optionId)) {
      updated = current.includes(optionId) ? current.filter((id) => id !== optionId) : [optionId];
    } else {
      const clean = current.filter((id) => !exclusiveIds.has(id));
      if (clean.includes(optionId)) updated = clean.filter((id) => id !== optionId);
      else if (question.maxSelect && clean.length >= question.maxSelect) {
        setValidationError(`Choose up to ${question.maxSelect}. Deselect one to add another.`);
        return;
      }
      else updated = [...clean, optionId];
    }
    if (otherOption && current.includes(otherOption.id) && !updated.includes(otherOption.id)) clearOtherText(question.id);
    setAnswers((state) => ({ ...state, [question.id]: updated }));
    setSkipped((state) => { const copy = new Set(state); copy.delete(question.id); return copy; });
    setValidationError("");
    void persistAnswer(question.id, updated, false).catch(() => undefined);
  };

  const updateText = (questionId: string, value: string) => {
    setAnswers((state) => ({ ...state, [questionId]: value }));
    setSkipped((state) => { const copy = new Set(state); copy.delete(questionId); return copy; });
    window.clearTimeout(textTimer.current);
    textTimer.current = window.setTimeout(() => {
      void persistAnswer(questionId, value, false).catch(() => undefined);
    }, 450);
  };

  const validateAndNext = async () => {
    if (step.required && !isAnswered(answers[step.id])) {
      setValidationError("Please answer to continue.");
      return;
    }
    const otherOption = step.options?.find((option) => option.label.toLowerCase() === "other");
    const selected = Array.isArray(answers[step.id]) ? answers[step.id].includes(otherOption?.id ?? "") : answers[step.id] === otherOption?.id;
    if (otherOption && selected) {
      const textId = otherTextQuestionId(step.id);
      const value = String(answers[textId] ?? "").trim();
      if (!value) {
        setValidationError("Please tell us what ‘Other’ means for your team.");
        return;
      }
      window.clearTimeout(textTimer.current);
      try { await persistAnswer(textId, value, false); } catch { return; }
    }
    if (step.type === "short") {
      window.clearTimeout(textTimer.current);
      const value = String(answers[step.id] ?? "");
      if (value) {
        try { await persistAnswer(step.id, value, false); } catch { return; }
      }
    }
    next();
  };

  const skip = async () => {
    setSkipped((state) => new Set(state).add(step.id));
    setAnswers((state) => { const copy = { ...state }; delete copy[step.id]; return copy; });
    try {
      await persistAnswer(step.id, null, true);
      if (step.type === "contact") await sendEvent("contact_skipped");
      else if (step.type === "giftcard") await sendEvent("gift_claim_skipped");
      next();
    } catch {
      // Remain until the skip is safely stored.
    }
  };

  const submitContactForm = async () => {
    const selected = Array.isArray(answers.nextstep) ? answers.nextstep : [];
    const wantsFollowUp = selected.length > 0 && !selected.includes("o5");
    if (wantsFollowUp && (!contact.followUpEmail.includes("@") || !contact.researchConsent)) {
      setValidationError("Add a valid email and agree to follow-up, or skip this step.");
      return;
    }
    if (contact.marketingConsent && !contact.followUpEmail.includes("@")) {
      setValidationError("Add a valid email for occasional updates.");
      return;
    }
    setPendingSaves((count) => count + 1);
    try {
      await saveContact(session!, contact);
      setGift((current) => ({ ...current, name: current.name || contact.name, company: current.company || contact.company, giftEmail: current.giftEmail || contact.followUpEmail }));
      next();
    } catch {
      setSaveError("Contact details were not saved. Please try again.");
    } finally {
      setPendingSaves((count) => Math.max(0, count - 1));
    }
  };

  const submitGift = async () => {
    if (!gift.name.trim() || !gift.company.trim() || !gift.giftEmail.includes("@")) {
      setValidationError("Enter your name, company, and a valid gift-card email.");
      return;
    }
    setPendingSaves((count) => count + 1);
    try {
      const result = await submitGiftClaim(session!, gift);
      setGiftStatus(result.status);
      next();
    } catch {
      setSaveError("Your gift-card claim was not saved. Please try again.");
    } finally {
      setPendingSaves((count) => Math.max(0, count - 1));
    }
  };

  if (loading) return <div className="loading-screen"><OEMReadyLogo /><span className="spinner" /><p>Preparing your experience…</p></div>;
  if (fatalError || !session) return <div className="loading-screen error-screen"><OEMReadyLogo /><h1>We couldn’t open this page</h1><p>{fatalError}</p><button className="primary-button" onClick={() => window.location.reload()}>Try again</button></div>;

  const showHeader = !["landing", "completion"].includes(step.type);
  const currentOptions = optionsForStep(step, answers);
  const answer = answers[step.id];
  const otherOption = currentOptions.find((option) => option.label.toLowerCase() === "other");
  const otherSelected = Boolean(otherOption && (Array.isArray(answer) ? answer.includes(otherOption.id) : answer === otherOption.id));
  const saveLabel = saveError || (pendingSaves > 0 ? "Saving…" : lastSavedAt ? "Answers saved" : "Secure autosave on");

  return (
    <div className="page-shell">
      <div className="survey-shell">
        {showHeader && <Header step={step} index={safeIndex} total={steps.length} onBack={back} canGoBack={safeIndex > 0} />}
        <main className={`survey-main step-${step.type}`}>
          {step.type === "landing" && (
            <section className="landing-card">
              <OEMReadyLogo />
              <span className="audience-tag">Built for commercial service teams</span>
              <h1>{step.headline}</h1>
              <p className="landing-body">{step.body}</p>
              <div className="landing-facts">
                <article><strong>4 min</strong><span>Guided experience</span></article>
                <article><strong>75 sec</strong><span>Product preview</span></article>
                <article className="gift-fact"><strong>{GIFT_AMOUNT}</strong><span>Thank-you gift card</span></article>
              </div>
              <div className="trust-note"><span aria-hidden="true">✓</span><p><strong>Your input stays private</strong><small>Contact details are optional, stored separately, and never used for marketing without your permission.</small></p></div>
              <button className="primary-button landing-cta" onClick={() => { void sendEvent("survey_started", { variant }); next(); }}>Start the OEMReady walkthrough <span aria-hidden="true">→</span></button>
              <p className="landing-meta">No account required <i>•</i> Answers save automatically <i>•</i> One {GIFT_AMOUNT} gift card per company</p>
            </section>
          )}

          {(["single", "multi", "ranked", "nextstep"].includes(step.type)) && (
            <section className="question-panel">
              <h1>{step.title}</h1>
              {step.subtitle && <p className="question-subtitle">{step.subtitle}</p>}
              <div className="option-list">
                {currentOptions.map((option) => {
                  const selected = Array.isArray(answer) ? answer.includes(option.id) : answer === option.id;
                  const multi = step.type === "multi" || step.type === "nextstep";
                  return <OptionButton key={option.id} label={option.label} selected={selected} multi={multi} onClick={() => multi ? toggleMulti(step, option.id) : void chooseSingle(step, option.id)} />;
                })}
              </div>
              {otherSelected && (
                <div className="other-detail">
                  <label htmlFor={`other-${step.id}`}>Tell us more</label>
                  <textarea
                    id={`other-${step.id}`}
                    value={String(answers[otherTextQuestionId(step.id)] ?? "")}
                    onChange={(event) => updateText(otherTextQuestionId(step.id), event.target.value)}
                    placeholder="Add a short answer"
                    rows={3}
                    autoFocus
                  />
                  <small>Saved privately as you type.</small>
                </div>
              )}
              {step.maxSelect && <p className="selection-count">{Array.isArray(answer) ? answer.length : 0} of {step.maxSelect} selected</p>}
            </section>
          )}

          {step.type === "short" && (
            <section className="question-panel">
              <h1>{step.title}</h1>
              {step.subtitle && <p className="question-subtitle">{step.subtitle}</p>}
              <label className="sr-only" htmlFor={`answer-${step.id}`}>{step.title}</label>
              <textarea id={`answer-${step.id}`} value={String(answer ?? "")} onChange={(event) => updateText(step.id, event.target.value)} placeholder={step.placeholder} rows={6} />
              <p className="dictation-tip">Your phone’s microphone button works here too. Open-text responses are stored privately and never sent to PostHog.</p>
            </section>
          )}

          {step.type === "transition" && (
            <section className="transition-panel"><span className="transition-icon">✓</span><h1>{step.title}</h1><p>{step.body}</p><span>Concept length: 75 seconds</span><button className="primary-button" onClick={next}>Continue</button></section>
          )}

          {step.type === "demo" && (
            <section className="demo-panel">
              <h1>{step.title}</h1>
              <p>Watch a 75-second preview of the proposed workflow. You’ll stay in control at every step.</p>
              <ConceptDemo onEvent={(name, metadata) => void sendEvent(name, metadata)} onReadyToContinue={() => setDemoReady(true)} />
              <button className="primary-button" disabled={!demoReady} onClick={next}>Continue</button>
              {!demoReady && <button className="text-button" onClick={() => { setDemoReady(true); void sendEvent("demo_bypassed", { progress_requirement_bypassed: true }); }}>Continue without watching the full preview</button>}
            </section>
          )}

          {step.type === "contact" && (
            <section className="form-panel">
              <h1>{step.title}</h1>
              <p>This step is optional. Your contact details are stored separately and used only for the follow-up you request.</p>
              <div className="form-grid">
                <label>Name<input value={contact.name} onChange={(event) => setContact({ ...contact, name: event.target.value })} autoComplete="name" /></label>
                <label>Company<input value={contact.company} onChange={(event) => setContact({ ...contact, company: event.target.value })} autoComplete="organization" /></label>
                <label>Follow-up email<input type="email" value={contact.followUpEmail} onChange={(event) => setContact({ ...contact, followUpEmail: event.target.value })} autoComplete="email" /></label>
                <label>Phone <span>(optional)</span><input type="tel" value={contact.phone} onChange={(event) => setContact({ ...contact, phone: event.target.value })} autoComplete="tel" /></label>
                <label>Preferred contact method<select value={contact.preferredMethod} onChange={(event) => setContact({ ...contact, preferredMethod: event.target.value as ContactPayload["preferredMethod"] })}><option value="">Choose one</option><option value="email">Email</option><option value="phone">Phone</option><option value="text">Text</option></select></label>
              </div>
              <label className="consent-row"><input type="checkbox" checked={contact.researchConsent} onChange={(event) => setContact({ ...contact, researchConsent: event.target.checked })} /><span>I agree to be contacted about the next steps I selected.</span></label>
              <label className="consent-row"><input type="checkbox" checked={contact.marketingConsent} onChange={(event) => setContact({ ...contact, marketingConsent: event.target.checked })} /><span>I’d also like occasional OEMReady updates. This is separate from follow-up and is not required for the gift card.</span></label>
            </section>
          )}

          {step.type === "giftcard" && (
            <section className="gift-panel">
              <span className="gift-value">{GIFT_AMOUNT}</span>
              <h1>{step.title}</h1>
              <p>Thank you for helping us build a better warranty workflow. Enter your details below to receive one {GIFT_AMOUNT} gift card per company.</p>
              <div className="form-grid">
                <label>Name<input value={gift.name} onChange={(event) => setGift({ ...gift, name: event.target.value })} autoComplete="name" /></label>
                <label>Company name<input value={gift.company} onChange={(event) => setGift({ ...gift, company: event.target.value })} autoComplete="organization" /></label>
                <label>Email for the gift card<input type="email" value={gift.giftEmail} onChange={(event) => setGift({ ...gift, giftEmail: event.target.value })} autoComplete="email" /></label>
              </div>
              <div className="gift-assurance"><span aria-hidden="true">✓</span><small>Gift-card claims are reviewed and fulfilled manually. This email is used for fulfillment only and does not opt you into marketing.</small></div>
            </section>
          )}

          {step.type === "completion" && (
            <section className="completion-panel">
              <div className="completion-mark">✓</div>
              <h1>{step.title ?? "Thank you—your input is in"}</h1>
              <p>{step.body ?? "Your experience will help shape an OEMReady workflow that works for real service teams."}</p>
              {step.id !== "screened_out" && giftStatus && <p className="completion-detail">Your gift-card claim is recorded as <strong>{giftStatus}</strong>. Claims are reviewed before fulfillment.</p>}
              <span>You can close this page now.</span>
            </section>
          )}
        </main>

        {!['landing', 'transition', 'demo', 'completion'].includes(step.type) && (
          <footer className="survey-footer">
            {validationError && <p className="validation-error" role="alert">{validationError}</p>}
            {saveError && <p className="save-error" role="alert">{saveError}</p>}
            {(["multi", "ranked", "short", "nextstep"].includes(step.type) || (step.type === "single" && otherSelected)) && <button className="primary-button" onClick={() => void validateAndNext()}>Continue</button>}
            {step.type === "contact" && <button className="primary-button" onClick={() => void submitContactForm()} disabled={pendingSaves > 0}>Save &amp; continue</button>}
            {step.type === "giftcard" && <button className="primary-button" onClick={() => void submitGift()} disabled={pendingSaves > 0}>Claim my {GIFT_AMOUNT} gift card</button>}
            {(step.skippable || step.type === "contact" || step.type === "giftcard") && <button className="text-button" onClick={() => void skip()}>{step.type === "giftcard" ? "Finish without a gift card" : "Skip this step"}</button>}
            <span className={`save-indicator ${saveError ? "has-error" : ""}`}>{pendingSaves > 0 ? "◌" : saveError ? "!" : "✓"} {saveLabel}</span>
          </footer>
        )}
      </div>
    </div>
  );
}
